import React, { Fragment, useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import Scrollbar from "../../components/scrollbar/scrollbar";
import {
  addToCart,
  addToWishList,
  addToCompareList,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
} from "../../store/actions/action";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/footer/Footer";
import FilterSidebar from "../../components/FilterSidebar";
import FilterAllProduct from "../../components/FilterAllProduct";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import _ from "lodash";

const ShopPage = ({
  addToCart,
  addToWishList,
  addToCompareList,
  carts,
  ...props
}) => {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [filter, setFilter] = useState({
    size: searchParams.get("size") || "",
    category: searchParams.get("category") || "",
    brand: searchParams.get("brand") || "",
    page: searchParams.get("page") || 1,
    price: searchParams.get("price") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    minPrice: searchParams.get("minPrice") || "",
    iseuro: searchParams.get("iseuro") || "",
    sort: searchParams.get("sort") || "featured",
    limit: 12,
  });

  const getPrevParams = () =>
    Object.keys(filter)?.reduce((acc, key) => {
      if (searchParams.get(key)) acc[key] = searchParams.get(key);
      return acc;
    }, {});

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    const prevousPrams = getPrevParams();
    setSearchParams({ ...prevousPrams, page: value });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const priceChangeHandler = (clickedObject) => {
    const newParams = new URLSearchParams(searchParams);
    if (_?.isEmpty(clickedObject)) {
      setFilter({ ...filter, page: 1, minPrice: "", maxPrice: "" });
      newParams.delete("minPrice");
      newParams.delete("maxPrice");
      setSearchParams(newParams);
      return;
    }
    setFilter({
      ...filter,
      page: 1,
      minPrice: clickedObject?.minPrice || "",
      maxPrice:
        clickedObject?.maxPrice !== null ? clickedObject?.maxPrice : "",
    });
    if (clickedObject?.minPrice) newParams.set("minPrice", clickedObject?.minPrice);
    else newParams.delete("minPrice");
    if (clickedObject?.maxPrice !== null)
      newParams.set("maxPrice", clickedObject?.maxPrice);
    else newParams.delete("maxPrice");
    setSearchParams(newParams);
  };

  const changeHandler = ({ target: { name, value } }) => {
    let prevousPrams = getPrevParams();
    if (name === "category") delete prevousPrams.brand;
    setSearchParams({ ...prevousPrams, page: 1, [name]: value });
    setFilter((prev) => {
      const updated = { ...prev, page: 1, [name]: value };
      if (name === "category") updated.brand = "";
      return updated;
    });
  };

  const clearAll = () => {
    const newParams = new URLSearchParams();
    setSearchParams(newParams);
    setFilter((prev) => ({
      ...prev,
      page: 1,
      category: "",
      brand: "",
      minPrice: "",
      maxPrice: "",
      size: "",
      iseuro: "",
      sort: "featured",
    }));
  };

  const handleSortChange = (value) => {
    const prevousPrams = getPrevParams();
    const newParams = { ...prevousPrams, page: 1 };
    if (value && value !== "featured") {
      newParams.sort = value;
    } else {
      delete newParams.sort;
    }
    setSearchParams(newParams);
    setFilter((prev) => ({ ...prev, page: 1, sort: value || "featured" }));
  };

  const removeFilter = (name) => {
    if (name === "price") {
      priceChangeHandler({});
      return;
    }
    changeHandler({ target: { name, value: "" } });
  };

  const addToCartProduct = async (product) => {
    await addToCart(product, 1, filter.category, filter.size);
  };

  const onRegionChange = async (value) => {
    let prevousPrams = getPrevParams();
    if (value === "eu") {
      setFilter((prev) => ({ ...prev, page: 1, iseuro: true }));
      setSearchParams({ ...prevousPrams, iseuro: "true", page: 1 });
    } else {
      setFilter((prev) => ({ ...prev, page: 1, iseuro: "" }));
      searchParams.delete("iseuro");
      setSearchParams(searchParams);
      fetchProducts();
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.price,
    filter.size,
    filter.category,
    filter.iseuro,
    filter?.brand,
    filter?.page,
    filter?.minPrice,
    filter?.maxPrice,
    filter?.sort,
  ]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const fetchProducts = () => {
    setProductsLoading(true);
    if (productsError) setProductsError(false);
    axios
      .get("get-products", { params: filter })
      .then((response) => {
        const data = response?.data?.data;
        setProducts(data?.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch((error) => {
        setProducts([]);
        setProductsError(true);
        const apiTotal = error?.response?.data?.totalRecords;
        setFilter((prev) => ({
          ...prev,
          totalRecords: typeof apiTotal === "number" ? apiTotal : 0,
        }));
      })
      .finally(() => setProductsLoading(false));
  };

  const addToWishListProduct = (product, qty = 1) =>
    addToWishList(product, qty);
  const addToCompareListProduct = (product, qty = 1) =>
    addToCompareList(product, qty);

  // Active filter chips (computed from filter state)
  const activeChips = useMemo(() => {
    const chips = [];
    if (filter?.category)
      chips.push({ key: "category", label: filter.category });
    if (filter?.brand) chips.push({ key: "brand", label: filter.brand });
    if (filter?.minPrice || filter?.maxPrice) {
      const lo = filter?.minPrice ? `AED ${filter.minPrice}` : "AED 0";
      const hi = filter?.maxPrice ? `AED ${filter.maxPrice}` : "and up";
      chips.push({ key: "price", label: `${lo} – ${hi}` });
    }
    return chips;
  }, [filter?.category, filter?.brand, filter?.minPrice, filter?.maxPrice]);

  // Quick category chips for hero
  const quickCategories = useMemo(() => {
    const list = props?.configs?.categories || [];
    const seen = new Set();
    const out = [];
    for (const c of list) {
      const name = c?.name?.trim();
      if (!name || name.toLowerCase() === "all category") continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
      if (out.length >= 8) break;
    }
    return out;
  }, [props?.configs?.categories]);

  return (
    <Fragment>
      <Navbar />

      {/* HERO STRIP */}
      <section className="gpshop-hero">
        <div className="gpshop-hero-inner">
          <div className="gpshop-hero-text">
            <div className="gpshop-hero-eyebrow">SHOP</div>
            <h1 className="gpshop-hero-title">
              Browse <span>Gift Cards</span>
            </h1>
            <p className="gpshop-hero-sub">
              Discover premium gift cards from your favourite brands — instant
              delivery, verified partners, and trusted by 10,000+ customers.
            </p>
          </div>

          {quickCategories.length > 0 && (
            <div className="gpshop-hero-chips">
              <button
                className={`gpshop-chip ${
                  !filter?.category ? "gpshop-chip-active" : ""
                }`}
                onClick={() =>
                  changeHandler({ target: { name: "category", value: "" } })
                }
              >
                All
              </button>
              {quickCategories.map((c, i) => {
                const isActive = filter?.category === c?.name;
                return (
                  <button
                    key={c?._id || c?.name || i}
                    className={`gpshop-chip ${
                      isActive ? "gpshop-chip-active" : ""
                    }`}
                    onClick={() =>
                      changeHandler({
                        target: { name: "category", value: c?.name },
                      })
                    }
                  >
                    {c?.url && (
                      <img src={c.url} alt="" className="gpshop-chip-img" />
                    )}
                    {c?.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ACTIVE FILTERS */}
      {activeChips.length > 0 && (
        <div className="gpshop-active-bar">
          <div className="gpshop-active-inner">
            <span className="gpshop-active-label">Active filters:</span>
            <div className="gpshop-active-chips">
              {activeChips.map((chip) => (
                <button
                  key={chip.key}
                  className="gpshop-active-chip"
                  onClick={() => removeFilter(chip.key)}
                  title={`Remove ${chip.label}`}
                >
                  {chip.label}
                  <span className="gpshop-active-x" aria-hidden="true">
                    ×
                  </span>
                </button>
              ))}
              <button className="gpshop-active-clear" onClick={clearAll}>
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAIN */}
      <div className="shop-section gpshop-main">
        <div className="sm-container">
          <div className="row">
            <FilterSidebar
              filter={filter}
              priceChangeHandler={priceChangeHandler}
              addToCompareListProduct={addToCompareListProduct}
              changeHandler={changeHandler}
              onClearAll={clearAll}
              {...props}
            />
            <FilterAllProduct
              addToCartProduct={addToCartProduct}
              addToWishListProduct={addToWishListProduct}
              addToCompareListProduct={addToCompareListProduct}
              products={products}
              page={filter?.page}
              handlePageChange={handlePageChange}
              carts={carts}
              removeFromCart={removeFromCart}
              decrementQuantity={decrementQuantity}
              incrementQuantity={incrementQuantity}
              loading={productsLoading}
              filter={filter}
              iseuro={filter?.iseuro}
              onRegionChange={onRegionChange}
              sort={filter?.sort}
              onSortChange={handleSortChange}
              onOpenFilterDrawer={() => setDrawerOpen(true)}
              onClearAll={clearAll}
              {...props}
            />
          </div>
        </div>
      </div>

      {/* MOBILE FILTER DRAWER */}
      {drawerOpen && (
        <div
          className="gpshop-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="gpshop-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <FilterSidebar
              filter={filter}
              priceChangeHandler={priceChangeHandler}
              changeHandler={changeHandler}
              onClearAll={clearAll}
              isDrawer
              onCloseDrawer={() => setDrawerOpen(false)}
              {...props}
            />
          </div>
        </div>
      )}

      <Footer />
      <Scrollbar />

      <style>{`
        .gpshop-hero {
          background:
            radial-gradient(circle at 90% 0%, rgba(212, 168, 87, 0.12), transparent 45%),
            radial-gradient(circle at 0% 100%, rgba(212, 168, 87, 0.06), transparent 50%),
            linear-gradient(135deg, #0b0b0c 0%, #15110a 100%);
          color: #fff;
          padding: 22px 20px 18px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.18);
        }
        .gpshop-hero-inner {
          max-width: 1300px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(280px, 460px) 1fr;
          gap: 28px;
          align-items: center;
        }
        .gpshop-hero-eyebrow {
          font-size: 10px;
          letter-spacing: 2.5px;
          color: #d4a857;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .gpshop-hero-title {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 4px;
          line-height: 1.15;
        }
        .gpshop-hero-title span {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpshop-hero-sub {
          font-size: 12px;
          color: #9aa0a8;
          margin: 0;
          line-height: 1.5;
        }

        .gpshop-hero-chips {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .gpshop-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(212, 168, 87, 0.25);
          color: #e6e8ec;
          padding: 6px 13px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all .2s ease;
          backdrop-filter: blur(4px);
          white-space: nowrap;
        }
        .gpshop-chip:hover {
          background: rgba(212, 168, 87, 0.18);
          border-color: #d4a857;
          color: #fff;
        }
        .gpshop-chip-active {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          border-color: transparent;
          color: #1a1208;
          font-weight: 700;
          box-shadow: 0 4px 10px rgba(212, 168, 87, 0.35);
        }
        .gpshop-chip-active:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857);
          color: #1a1208;
        }
        .gpshop-chip-img {
          width: 14px;
          height: 14px;
          border-radius: 4px;
          object-fit: cover;
          background: rgba(255,255,255,0.2);
        }

        .gpshop-active-bar {
          background: #15110a;
          border-bottom: 1px solid rgba(212, 168, 87, 0.18);
          padding: 10px 20px;
        }
        .gpshop-active-inner {
          max-width: 1300px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }
        .gpshop-active-label {
          font-size: 11px;
          color: #9aa0a8;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 600;
        }
        .gpshop-active-chips {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: center;
        }
        .gpshop-active-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.5);
          color: #d4a857;
          padding: 4px 6px 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: background .2s ease, color .2s ease, transform .15s ease;
        }
        .gpshop-active-chip:hover {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          transform: translateY(-1px);
        }
        .gpshop-active-x {
          font-size: 14px;
          line-height: 1;
          padding: 0 4px;
          font-weight: 400;
        }
        .gpshop-active-clear {
          background: transparent;
          border: none;
          color: #9aa0a8;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 4px;
        }
        .gpshop-active-clear:hover { color: #d4a857; }

        .gpshop-main {
          background: #0b0b0c;
          padding: 22px 0 60px;
          min-height: 60vh;
        }
        .gpshop-main .sm-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 20px;
        }

        /* DRAWER */
        .gpshop-drawer-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 1500;
          display: flex;
          animation: gpshop-fadein .2s ease;
        }
        @keyframes gpshop-fadein {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .gpshop-drawer {
          background: #0b0b0c;
          width: min(88vw, 360px);
          height: 100%;
          overflow-y: auto;
          padding: 18px;
          box-shadow: 4px 0 24px rgba(0,0,0,0.5);
          animation: gpshop-slidein .25s ease;
        }
        @keyframes gpshop-slidein {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        @media (max-width: 991px) {
          .gpshop-hero {
            padding: 18px 16px;
          }
          .gpshop-hero-inner {
            grid-template-columns: 1fr;
            gap: 14px;
          }
          .gpshop-hero-chips {
            justify-content: flex-start;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 4px;
            margin: 0 -16px;
            padding-left: 16px;
            padding-right: 16px;
            scrollbar-width: none;
          }
          .gpshop-hero-chips::-webkit-scrollbar { display: none; }
          .gpshop-hero-title { font-size: 20px; }
          .gpshop-main { padding: 14px 0 40px; }
          .gpshop-main .sm-container { padding: 0 14px; }
        }
        @media (max-width: 575px) {
          .gpshop-hero { padding: 16px 14px; }
          .gpshop-hero-title { font-size: 18px; }
          .gpshop-hero-eyebrow { font-size: 10px; letter-spacing: 2px; }
          .gpshop-active-bar { padding: 8px 14px; }
        }
      `}</style>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  carts: state.cartList.cart,
  w_list: state.wishList.w_list,
  configs: state.configs,
});

export default connect(mapStateToProps, {
  addToCart,
  addToWishList,
  addToCompareList,
  removeFromCart,
  incrementQuantity,
  decrementQuantity,
})(ShopPage);
